import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { Input, Select } from '../ui/Input';
import Modal from '../ui/Modal';
import './ProductAssemblyPreview.css';

const ASSEMBLY_VIEW = Object.freeze({
  FRONT: 'front',
  ISOMETRIC: 'isometric',
});

const ASSEMBLY_VIEW_OPTIONS = [
  { key: ASSEMBLY_VIEW.FRONT, label: 'Frontal' },
  { key: ASSEMBLY_VIEW.ISOMETRIC, label: 'Isométrica' },
];

const ASSEMBLY_VIEW_API_VALUES = Object.freeze({
  [ASSEMBLY_VIEW.FRONT]: 'front_xz',
  [ASSEMBLY_VIEW.ISOMETRIC]: 'isometric_xyz',
});

const ASSEMBLY_VIEW_ID_LABELS = Object.freeze({
  front_xz: 'Vista frontal',
  isometric_xyz: 'Vista isométrica',
});

const EMPTY_FORM = {
  leftSidePieceId: '',
  rightSidePieceId: '',
  shelfPieceId: '',
  shelfBaseHeightMm: '',
  view: ASSEMBLY_VIEW.FRONT,
};

const ROLE_LABELS = {
  leftSidePieceId: 'Lateral esquerda',
  rightSidePieceId: 'Lateral direita',
  shelfPieceId: 'Prateleira',
};

const numberFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});

function formatMeasurement(value) {
  const number = Number(value);
  return Number.isFinite(number) ? numberFormatter.format(number) : '—';
}

function formatDimensions(piece) {
  if (!piece) return 'Selecione uma peça';
  return `${formatMeasurement(piece.comprimento)} × ${formatMeasurement(piece.largura)} × ${formatMeasurement(piece.espessura)} mm`;
}

function pieceLabel(piece) {
  const identity = [piece.codigo, piece.nome || piece.descricao]
    .filter(Boolean)
    .join(' / ') || `Peça #${piece.id}`;
  return `${identity} — ${formatDimensions(piece)}`;
}

function errorFromResponse(error) {
  const detail = error.response?.data?.detail;
  const code = detail?.code;

  if (code === 'ASSEMBLY_PRODUCT_NOT_FOUND') {
    return {
      message: 'O produto não foi encontrado. Recarregue a página e tente novamente.',
      issues: [],
    };
  }
  if (code === 'ASSEMBLY_PIECE_NOT_FOUND') {
    return {
      message: 'Uma das peças selecionadas não está disponível neste produto. Revise a seleção.',
      issues: [],
    };
  }
  if (code === 'ASSEMBLY_SHELF_HEIGHT_OUT_OF_RANGE') {
    const minimum = Number(detail.minimum_mm);
    const maximum = Number(detail.maximum_mm);
    const range = Number.isFinite(minimum) && Number.isFinite(maximum)
      ? ` Use um valor entre ${formatMeasurement(minimum)} e ${formatMeasurement(maximum)} mm.`
      : '';
    return {
      message: `A altura da prateleira está fora da faixa permitida.${range}`,
      issues: [],
    };
  }
  if (code === 'ASSEMBLY_VALIDATION_FAILED') {
    const issues = Array.isArray(detail.issues)
      ? detail.issues
        .map((issue) => issue?.message)
        .filter((message) => typeof message === 'string' && message.trim())
      : [];
    return {
      message: 'A montagem não pôde ser validada com as peças selecionadas.',
      issues,
    };
  }

  return {
    message: typeof detail === 'string'
      ? detail
      : 'Não foi possível gerar o preview de montagem.',
    issues: [],
  };
}

function ProductAssemblyPreview({ productId, pieces }) {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const blobUrlRef = useRef(null);
  const requestInFlightRef = useRef(false);
  const requestVersionRef = useRef(0);

  const piecesById = useMemo(
    () => new Map(pieces.map((piece) => [String(piece.id), piece])),
    [pieces]
  );

  const revokePreviewUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const clearPreview = useCallback(() => {
    revokePreviewUrl();
    setPreview(null);
  }, [revokePreviewUrl]);

  useEffect(() => () => revokePreviewUrl(), [revokePreviewUrl]);

  const invalidatePreview = () => {
    requestVersionRef.current += 1;
    clearPreview();
    setError(null);
  };

  const updateField = (field) => (event) => {
    invalidatePreview();
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const closeModal = () => {
    requestVersionRef.current += 1;
    clearPreview();
    setLoading(false);
    setError(null);
    setForm(EMPTY_FORM);
    setIsOpen(false);
  };

  const validateForm = () => {
    if (!form.leftSidePieceId || !form.rightSidePieceId || !form.shelfPieceId) {
      return 'Selecione a lateral esquerda, a lateral direita e a prateleira.';
    }
    if (form.shelfBaseHeightMm.trim() === '') {
      return 'Informe a altura da face inferior da prateleira.';
    }
    const height = Number(form.shelfBaseHeightMm);
    if (!Number.isFinite(height)) {
      return 'Informe uma altura numérica e finita.';
    }
    if (height < 0) {
      return 'A altura da prateleira não pode ser negativa.';
    }
    return null;
  };

  const requestPreview = async (event) => {
    event.preventDefault();
    if (requestInFlightRef.current) return;

    const validationError = validateForm();
    if (validationError) {
      setError({ message: validationError, issues: [] });
      return;
    }

    const payload = {
      left_side_peca_id: Number(form.leftSidePieceId),
      right_side_peca_id: Number(form.rightSidePieceId),
      shelf_peca_id: Number(form.shelfPieceId),
      shelf_base_height_mm: Number(form.shelfBaseHeightMm),
      view: ASSEMBLY_VIEW_API_VALUES[form.view],
    };
    const requestVersion = requestVersionRef.current;
    requestInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(
        `/api/produtos/${productId}/montagem/preview-from-selection`,
        payload
      );
      const data = response.data;
      if (!data || typeof data.svg !== 'string' || !data.svg.trim()) {
        throw new Error('Resposta inválida do preview de montagem.');
      }

      const blobUrl = URL.createObjectURL(new Blob(
        [data.svg],
        { type: 'image/svg+xml' }
      ));
      if (requestVersion !== requestVersionRef.current) {
        URL.revokeObjectURL(blobUrl);
        return;
      }

      revokePreviewUrl();
      blobUrlRef.current = blobUrl;
      setPreview({
        blobUrl,
        viewId: data.view_id,
        viewport: data.viewport,
        warnings: Array.isArray(data.warnings) ? data.warnings : [],
      });
    } catch (requestError) {
      console.error('ProductAssemblyPreview: falha ao gerar preview:', requestError);
      if (requestVersion === requestVersionRef.current) {
        setError(errorFromResponse(requestError));
      }
    } finally {
      requestInFlightRef.current = false;
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  };

  const selectedPieces = {
    leftSidePieceId: piecesById.get(form.leftSidePieceId),
    rightSidePieceId: piecesById.get(form.rightSidePieceId),
    shelfPieceId: piecesById.get(form.shelfPieceId),
  };
  const previewViewLabel = preview
    ? ASSEMBLY_VIEW_ID_LABELS[preview.viewId] || 'Vista de montagem'
    : null;

  return (
    <>
      <Card className="cw-product-assembly-action" aria-label="Ação de montagem do produto">
        <div className="cw-product-assembly-action-copy">
          <p className="cw-product-assembly-action-eyebrow">Montagem</p>
          <h2 className="cw-product-assembly-action-title">Esquema de montagem do produto</h2>
          <p className="cw-product-assembly-action-description">
            Combine duas laterais e uma prateleira usando as peças deste produto.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setIsOpen(true)}>
          Gerar esquema de montagem
        </Button>
      </Card>

      {isOpen && (
        <Modal onClose={closeModal} className="cw-product-assembly-modal">
          <header className="cw-product-assembly-header">
            <div>
              <p className="cw-product-assembly-eyebrow">Montagem do produto</p>
              <h2>Gerar esquema de montagem</h2>
              <p className="cw-product-assembly-intro">
                Escolha a vista, selecione as peças e informe a altura da prateleira.
              </p>
            </div>
            <Button variant="secondary" onClick={closeModal}>Fechar</Button>
          </header>

          <div className="cw-product-assembly-layout">
            <section className="cw-product-assembly-section" aria-labelledby="assembly-config-title">
              <h3 id="assembly-config-title">Configuração</h3>
              <form className="cw-product-assembly-form" onSubmit={requestPreview}>
                <fieldset className="cw-product-assembly-view-field">
                  <legend>Vista</legend>
                  <div className="cw-product-assembly-view-options">
                    {ASSEMBLY_VIEW_OPTIONS.map((option) => (
                      <label className="cw-product-assembly-view-option" key={option.key}>
                        <input
                          type="radio"
                          name="assembly-view"
                          value={option.key}
                          checked={form.view === option.key}
                          onChange={updateField('view')}
                          disabled={loading}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {Object.entries(ROLE_LABELS).map(([field, label]) => (
                  <div className="cw-product-assembly-field" key={field}>
                    <label htmlFor={`assembly-${field}`}>{label}</label>
                    <Select
                      id={`assembly-${field}`}
                      value={form[field]}
                      onChange={updateField(field)}
                      disabled={loading}
                    >
                      <option value="">Selecione uma peça</option>
                      {pieces.map((piece) => (
                        <option value={piece.id} key={piece.id}>{pieceLabel(piece)}</option>
                      ))}
                    </Select>
                  </div>
                ))}

                <div className="cw-product-assembly-field">
                  <label htmlFor="assembly-shelf-height">Altura da prateleira</label>
                  <div className="cw-product-assembly-height-control">
                    <Input
                      id="assembly-shelf-height"
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={form.shelfBaseHeightMm}
                      onChange={updateField('shelfBaseHeightMm')}
                      disabled={loading}
                    />
                    <span className="cw-product-assembly-unit">mm</span>
                  </div>
                  <p className="cw-product-assembly-help">
                    Face inferior da prateleira em relação à base da montagem.
                  </p>
                </div>
              </form>

              <dl className="cw-product-assembly-dimensions" aria-label="Dimensões selecionadas">
                {Object.entries(ROLE_LABELS).map(([field, label]) => (
                  <div className="cw-product-assembly-dimension" key={field}>
                    <dt>{label}</dt>
                    <dd>{formatDimensions(selectedPieces[field])}</dd>
                  </div>
                ))}
              </dl>

              {error && (
                <div className="cw-product-assembly-alert" role="alert">
                  <p>{error.message}</p>
                  {error.issues.length > 0 && (
                    <ul>
                      {error.issues.map((issue, index) => <li key={`${issue}:${index}`}>{issue}</li>)}
                    </ul>
                  )}
                </div>
              )}

              <Button
                className="cw-product-assembly-submit"
                type="submit"
                loading={loading}
                onClick={requestPreview}
              >
                Gerar preview
              </Button>
            </section>

            <section className="cw-product-assembly-section" aria-labelledby="assembly-preview-title">
              <h3 id="assembly-preview-title">Preview</h3>
              {loading && !preview && (
                <p className="cw-product-assembly-loading" role="status">
                  Gerando preview de montagem…
                </p>
              )}
              {!preview && !loading && (
                <p className="cw-product-assembly-preview-empty">
                  O esquema retornado pelo backend aparecerá aqui.
                </p>
              )}
              {preview && (
                <div className="cw-product-assembly-preview">
                  <div className="cw-product-assembly-preview-frame">
                    <img
                      src={preview.blobUrl}
                      alt={`${previewViewLabel} da montagem`}
                    />
                  </div>
                  <div className="cw-product-assembly-preview-meta">
                    <span>{previewViewLabel}</span>
                    {preview.viewport && (
                      <span>
                        Viewport: {formatMeasurement(preview.viewport.width_mm)} ×{' '}
                        {formatMeasurement(preview.viewport.height_mm)} mm
                      </span>
                    )}
                  </div>
                  {preview.warnings.length > 0 && (
                    <div className="cw-product-assembly-warnings">
                      <strong>Avisos</strong>
                      <ul>
                        {preview.warnings.map((warning, index) => (
                          <li key={`${warning.code || 'warning'}:${warning.subject || index}`}>
                            {warning.message || warning.code || 'Aviso da montagem'}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className="cw-product-assembly-actions">
            <Button variant="secondary" onClick={closeModal}>Fechar</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

export default ProductAssemblyPreview;
